import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { accountSessionApi } from '@/api/services/accountSession';
import type { AuthenticateResultModel } from '@/api/types/account';
import { ApiError } from '@/api/client/abpError';
import { TenantSwitcher } from '@/tenant/TenantSwitcher';
import { tenantStore } from '@/tenant/tenantStore';
import { tokenStorage } from '@/auth/tokenStorage';

type Step = 'credentials' | 'otp' | 'setup';

interface LocationState {
  from?: { pathname: string };
}

// 1:1 reproduction of the legacy Account/Login.cshtml inside the noa account
// layout (body.sign-in-basic-page.login-img + container > row.authentication >
// col > card), so it renders identically under the real theme CSS.
export function LoginPage() {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as LocationState | null)?.from?.pathname ?? '/';

  const [step, setStep] = useState<Step>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [otp, setOtp] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [setupInfo, setSetupInfo] = useState<{ sharedKey?: string | null; qr?: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Clear any stale backend `__tenant` cookie so a prior tenant session can't
    // break this sign-in (auth is header-based).
    tenantStore.syncCookie();
    const previous = document.body.className;
    document.body.className = 'sign-in-basic-page login-img';
    return () => {
      document.body.className = previous;
    };
  }, []);

  async function finishIfAuthenticated(result: AuthenticateResultModel): Promise<boolean> {
    if (result.accessToken) {
      await completeLogin();
      navigate(redirectTo, { replace: true });
      return true;
    }
    return false;
  }

  function handleChallenge(result: AuthenticateResultModel) {
    setMfaToken(result.mfaToken ?? '');
    if (result.requiresTwoFactorSetup) {
      setSetupInfo({ sharedKey: result.authenticatorSharedKey, qr: result.qrCodeDataUrl });
      setStep('setup');
    } else if (result.requiresTwoFactor) {
      setStep('otp');
    }
  }

  async function submitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    // Drop any leftover session (e.g. a prior host login) so its bearer token /
    // tenant can't ride along on this fresh sign-in. Align the __tenant cookie
    // with the chosen tenant (deletes it for host) so a stale cookie can't force
    // a tenant login.
    tokenStorage.clear();
    tenantStore.syncCookie();
    try {
      const result = await accountSessionApi.authenticate({
        userNameOrEmailAddress: username,
        password,
      });
      if (await finishIfAuthenticated(result)) return;
      if (result.requiresTwoFactor || result.requiresTwoFactorSetup) {
        handleChallenge(result);
        return;
      }
      setError(result.errorDescription || result.error || 'Invalid username or password.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function submitOtp(e: React.FormEvent, skipSetup = false) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await accountSessionApi.verifyTwoFactor({
        mfaToken,
        otp: skipSetup ? undefined : otp,
        skipTwoFactorSetup: skipSetup,
      });
      if (await finishIfAuthenticated(result)) return;
      setError(result.errorDescription || result.error || 'Verification failed.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verification failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container px-3">
      <div className="row justify-content-center align-items-center authentication authentication-basic h-100">
        <div className="col-xxl-4 col-xl-5 col-lg-5 col-md-6 col-sm-8 col-12">
          <TenantSwitcher />

          <div className="card mt-3 shadow-sm rounded">
            <div className="card-header d-flex border-bottom justify-content-center py-3">
              <a className="text-center" href="/">
                <img
                  className="header-brand-img"
                  src="/themes/finkit/logo/logo-horizontal-white.png"
                  alt="Finkit"
                />
              </a>
            </div>
            <div className="card-body m-4 pt-6 mt-0 position-relative">
              {error && <div className="alert alert-danger py-2">{error}</div>}

              {step === 'credentials' && (
                <form onSubmit={submitCredentials} className="mt-4" noValidate>
                  <div className="input-box mb-3">
                    <input
                      placeholder="Email"
                      className="form-control form-control-lg"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                    />
                    <span className="authentication-input-icon">
                      <i className="ri-mail-fill text-default fs-15 op-7" />
                    </span>
                  </div>

                  <div className="input-box mb-3">
                    <div className="input-group">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        className="form-control form-control-lg"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        className="input-group-text btn show-password"
                        type="button"
                        id="PasswordVisibilityButton"
                        onClick={() => setShowPassword((v) => !v)}
                      >
                        <i className={`align-middle ${showPassword ? 'ri-eye-line' : 'ri-eye-off-line'}`} aria-hidden="true" />
                      </button>
                    </div>
                    <span className="authentication-input-icon">
                      <i className="ri-lock-2-fill text-default fs-15 op-7" />
                    </span>
                  </div>

                  <div className="row">
                    <div className="col">
                      <div className="form-check mb-4">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="RememberMe"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="RememberMe">
                          Remember Me
                        </label>
                      </div>
                    </div>
                    <div className="col text-end">
                      <a href="/account/forgot-password">Forgot Password</a>
                    </div>
                  </div>

                  <div className="d-grid gap-2">
                    <button type="submit" className="btn btn-primary btn-lg mt-3" disabled={busy}>
                      {busy ? 'Signing in…' : 'Login'}
                    </button>
                  </div>
                </form>
              )}

              {step === 'setup' && (
                <div className="mt-4">
                  <p className="text-muted">
                    Set up two-factor authentication. Scan the QR code (or enter the key) in your
                    authenticator app, then enter the generated code.
                  </p>
                  {setupInfo?.qr && (
                    <div className="text-center mb-2">
                      <img src={setupInfo.qr} alt="Authenticator QR code" style={{ maxWidth: 180 }} />
                    </div>
                  )}
                  {setupInfo?.sharedKey && (
                    <p className="text-center">
                      <code>{setupInfo.sharedKey}</code>
                    </p>
                  )}
                </div>
              )}

              {(step === 'otp' || step === 'setup') && (
                <form onSubmit={(e) => submitOtp(e)} className="mt-2" noValidate>
                  <div className="input-box mb-3">
                    <input
                      placeholder="Authentication code"
                      className="form-control form-control-lg"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                    />
                    <span className="authentication-input-icon">
                      <i className="ri-shield-keyhole-fill text-default fs-15 op-7" />
                    </span>
                  </div>
                  <div className="d-grid gap-2">
                    <button type="submit" className="btn btn-primary btn-lg mt-2" disabled={busy}>
                      {busy ? 'Verifying…' : 'Verify'}
                    </button>
                    {step === 'setup' && (
                      <button
                        type="button"
                        className="btn btn-link"
                        disabled={busy}
                        onClick={(e) => submitOtp(e, true)}
                      >
                        Skip for now
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
